import axios from 'axios';

const getApiBase = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  // Prefer the page's own origin when the bundled env URL points to a
  // different preview deployment — Cloudflare/ingress routes /api to the
  // same backend regardless of subdomain, so this avoids stale env URLs
  // breaking the app in preview while still working in production.
  if (envUrl) {
    try {
      const envHost = new URL(envUrl).hostname;
      if (envHost !== window.location.hostname) {
        return window.location.origin;
      }
    } catch { /* malformed env, ignore */ }
  }
  return envUrl || window.location.origin;
};

const API = getApiBase();

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Inject the active-company header on every outgoing request so master_admin
// can switch tenants from the Companies page. Regular users have no
// `active_company_id` in localStorage so the header is omitted and the
// backend falls back to their own `user.company_id`.
api.interceptors.request.use((config) => {
  const active = localStorage.getItem('active_company_id');
  if (active) {
    config.headers = config.headers || {};
    config.headers['X-Active-Company'] = active;
  }
  return config;
});

// Response interceptor for token refresh.
// IMPORTANT: must not loop on public/auth pages where 401s from /system/brand
// or other authenticated calls are *expected* (user is simply not logged in).
const PUBLIC_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/track'];
const isOnPublicPage = () => {
  const p = window.location.pathname;
  return PUBLIC_PATHS.includes(p) || p.startsWith('/u/') || p.startsWith('/catalog/');
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Never try to refresh the refresh endpoint itself (would loop).
      if (originalRequest.url && originalRequest.url.includes('/auth/refresh')) {
        return Promise.reject(error);
      }
      // On public/auth pages a 401 is expected — just reject without redirect.
      if (isOnPublicPage()) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      try {
        await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed: send the user to login *only* if they're not already there.
        if (!isOnPublicPage()) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Clients
export const getClients = (params) => api.get('/clients', { params });
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);
export const setClientPortalAccess = (id, password) => api.post(`/clients/${id}/portal-access`, { password });
export const disableClientPortalAccess = (id) => api.delete(`/clients/${id}/portal-access`);
export const bulkDeleteClients = (ids) => api.post('/clients/bulk-delete', { client_ids: ids });
export const bulkUpdateClients = (payload) => api.post('/clients/bulk-update', payload);
export const getImportTemplateUrl = () => `${API}/api/clients/import/template`;
export const importClientsExcel = (formData) => api.post('/clients/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Beneficiaries (ID holders for orders)
export const getClientBeneficiaries = (clientId) => api.get(`/clients/${clientId}/beneficiaries`);
export const addClientBeneficiary = (clientId, data) => api.post(`/clients/${clientId}/beneficiaries`, data);
export const assignBeneficiaryFromClient = (dealerId, individualId) => api.post(`/clients/${dealerId}/beneficiaries/from-client/${individualId}`);
export const deleteBeneficiary = (id) => api.delete(`/beneficiaries/${id}`);
export const checkIdAvailability = (params) => api.get('/check-id-availability', { params });

// Client / End-client documents (admin-side)
export const listClientDocuments = (clientId) => api.get(`/clients/${clientId}/documents`);
export const uploadClientDocument = (clientId, formData) =>
  api.post(`/clients/${clientId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteClientDocument = (clientId, fileId) => api.delete(`/clients/${clientId}/documents/${fileId}`);

export const listBeneficiaryDocuments = (clientId, benId) =>
  api.get(`/clients/${clientId}/beneficiaries/${benId}/documents`);
export const uploadBeneficiaryDocument = (clientId, benId, formData) =>
  api.post(`/clients/${clientId}/beneficiaries/${benId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteBeneficiaryDocument = (clientId, benId, fileId) =>
  api.delete(`/clients/${clientId}/beneficiaries/${benId}/documents/${fileId}`);

// Client Portal
export const getPortalProfile = () => api.get('/portal/profile');
export const updatePortalProfile = (data) => api.put('/portal/profile', data);
export const getPortalOrders = () => api.get('/portal/orders');
export const getPortalOrder = (id) => api.get(`/portal/orders/${id}`);

// Dealer companies (B2B account management)
export const getDealers = () => api.get('/dealers');
export const getDealer = (id) => api.get(`/dealers/${id}`);
export const createDealer = (data) => api.post('/dealers', data);
export const updateDealer = (id, data) => api.put(`/dealers/${id}`, data);
export const deleteDealer = (id) => api.delete(`/dealers/${id}`);
export const getDealerUsers = (id) => api.get(`/dealers/${id}/users`);
export const createDealerUser = (id, data) => api.post(`/dealers/${id}/users`, data);
export const updateDealerUser = (id, uid, data) => api.put(`/dealers/${id}/users/${uid}`, data);
export const resetDealerUserPassword = (id, uid, data) => api.post(`/dealers/${id}/users/${uid}/reset-password`, data || {});
export const deleteDealerUser = (id, uid) => api.delete(`/dealers/${id}/users/${uid}`);

// Suppliers
export const getSuppliers = (params) => api.get('/suppliers', { params });
export const getSupplier = (id) => api.get(`/suppliers/${id}`);
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);

// Brands
export const getBrands = () => api.get('/brands');
export const createBrand = (data) => api.post('/brands', data);
export const updateBrand = (id, data) => api.put(`/brands/${id}`, data);
export const deleteBrand = (id) => api.delete(`/brands/${id}`);

// Models
export const getModels = (params) => api.get('/models', { params });
export const createModel = (data) => api.post('/models', data);
export const updateModel = (id, data) => api.put(`/models/${id}`, data);
export const deleteModel = (id) => api.delete(`/models/${id}`);

// Vehicles
export const getVehicles = (params) => api.get('/vehicles', { params });
export const getVehicle = (id) => api.get(`/vehicles/${id}`);
export const createVehicle = (data) => api.post('/vehicles', data);
export const updateVehicle = (id, data) => api.put(`/vehicles/${id}`, data);
export const deleteVehicle = (id) => api.delete(`/vehicles/${id}`);

// Shipping Companies
export const getShippingCompanies = (params) => api.get('/shipping-companies', { params });
export const getShippingCompany = (id) => api.get(`/shipping-companies/${id}`);
export const createShippingCompany = (data) => api.post('/shipping-companies', data);
export const updateShippingCompany = (id, data) => api.put(`/shipping-companies/${id}`, data);
export const deleteShippingCompany = (id) => api.delete(`/shipping-companies/${id}`);

// Orders
export const getOrders = (params) => api.get('/orders', { params });
export const getOrderStatusCounts = (params) => api.get('/orders/status-counts', { params });
export const getOrderBalance = (orderId) => api.get(`/orders/${orderId}/balance`);
export const getOrder = (id) => api.get(`/orders/${id}`);
export const getOrderTimeline = (id) => api.get(`/orders/${id}/timeline`);
export const createOrder = (data) => api.post('/orders', data);
export const getOrderImportTemplateUrl = () => `${API}/api/orders/import/template`;
export const previewOrdersImport = (formData) => api.post('/orders/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const aiPreviewOrdersImport = (formData) => api.post('/orders/import/ai-start', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
export const getAiImportStatus = (jobId) => api.get(`/orders/import/ai-status/${jobId}`);
export const confirmOrdersImport = (token) => api.post('/orders/import/confirm', { token });
export const getNewOrdersSummary = (status) => api.get('/orders/new-orders-summary', { params: status ? { status } : {} });
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const bulkOrdersAction = (payload) => api.post('/orders/bulk', payload);

// Order Cars
export const addCarToOrder = (orderId, data) => api.post(`/orders/${orderId}/cars`, data);
export const getCar = (orderId, carId) => api.get(`/orders/${orderId}/cars/${carId}`);
export const updateCar = (orderId, carId, data) => api.put(`/orders/${orderId}/cars/${carId}`, data);
export const deleteCar = (orderId, carId) => api.delete(`/orders/${orderId}/cars/${carId}`);

// Shipments
export const getShipments = (params) => api.get('/shipments', { params });
export const assignOrdersToContainer = (data) => api.post('/containers/assign', data);
export const getContainersView = (params) => api.get('/containers', { params });

// Stock Vehicles (for order assignment)
export const getStockVehicles = (params) => api.get('/stock-vehicles', { params });

// Purchase Order / Avis de Versement
export const getOrderPO = (orderId) => api.get(`/orders/${orderId}/po`);
export const getOrderPOPdfUrl = (orderId, carId) => {
  let url = `${API}/api/orders/${orderId}/po/pdf`;
  if (carId) url += `?car_id=${carId}`;
  return url;
};
export const getDeliveryNotePdfUrl = (orderId, carId) => {
  let url = `${API}/api/orders/${orderId}/delivery-note/pdf`;
  if (carId) url += `?car_id=${carId}`;
  return url;
};

// Payments
export const getOrderPayments = (orderId) => api.get(`/orders/${orderId}/payments`);
export const addPayment = (orderId, data) => api.post(`/orders/${orderId}/payments`, data);
export const deletePayment = (paymentId) => api.delete(`/payments/${paymentId}`);

// Send Avis de Versement via Email
export const sendAvisEmail = (orderId, data) => api.post(`/orders/${orderId}/send-avis`, data);

// Order Status
export const updateOrderStatus = (orderId, status) => api.put(`/orders/${orderId}/status?status=${status}`);

// Employees
export const getEmployees = () => api.get('/employees');
export const createEmployee = (data) => api.post('/employees', data);
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data);
export const deleteEmployee = (id) => api.delete(`/employees/${id}`);

// Duplicate-order detection & surgical cleanup
export const findDuplicateOrders = () => api.get('/orders-duplicates');
export const deleteOrderByMongoId = (mongoId) => api.delete(`/orders-duplicates/by-mongo/${mongoId}`);

// Files
export const uploadFile = (formData) => api.post('/files/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const getFiles = (params) => api.get('/files', { params });
export const deleteFile = (id) => api.delete(`/files/${id}`);
export const getFileUrl = (id) => `${API}/api/files/${id}`;

// Order documents + sharable client-upload links
export const listOrderFiles = (orderId) => api.get(`/orders/${orderId}/files`);
export const uploadOrderFile = (orderId, formData) =>
  api.post(`/orders/${orderId}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteOrderFile = (orderId, fileId) => api.delete(`/orders/${orderId}/files/${fileId}`);
export const createOrderUploadToken = (orderId, payload) => api.post(`/orders/${orderId}/upload-token`, payload || {});
export const listOrderUploadTokens = (orderId) => api.get(`/orders/${orderId}/upload-tokens`);
export const revokeOrderUploadToken = (orderId, tokenId) => api.post(`/orders/${orderId}/upload-tokens/${tokenId}/revoke`);

// Notifications
export const getNotifications = () => api.get('/notifications');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

// Telegram
export const getTelegramStatus = () => api.get('/telegram/status');
export const linkTelegram = () => api.post('/telegram/link');
export const unlinkTelegram = () => api.delete('/telegram/unlink');
export const testTelegram = () => api.post('/telegram/test');

// Zapier Webhooks
export const getZapierSettings = () => api.get('/settings/zapier');
export const updateZapierSettings = (data) => api.put('/settings/zapier', data);
export const testZapierWebhook = (url) => api.post(`/settings/zapier/test?webhook_url=${encodeURIComponent(url)}`);
export const syncAllToZapier = () => api.post('/sync/zapier');

// Excel Export
export const getExportExcelUrl = () => `${API}/api/export/excel`;

// Reports
export const getAgentReports = (params) => api.get('/reports/agents', { params });
export const getReportsSummary = (params) => api.get('/reports/summary', { params });

// Container Tracking (Terminal49)
export const createTrackingRequest = (containerNumber, scac, orderId, carId) =>
  api.post(`/tracking/request?container_number=${containerNumber}&scac=${scac}${orderId ? `&order_id=${orderId}` : ''}${carId ? `&car_id=${carId}` : ''}`);
export const getTrackingStatus = (trackingId) => api.get(`/tracking/${trackingId}`);
export const getTrackingByContainer = (containerNumber) => api.get(`/tracking/by-container/${containerNumber}`);
export const getOrderTracking = (orderId) => api.get(`/tracking/order/${orderId}`);
export const getScacCodes = () => api.get('/tracking/scac-codes');
export const listShippingLines = () => api.get('/shipping-lines');
export const createShippingLine = (data) => api.post('/shipping-lines', data);
export const deleteShippingLine = (id) => api.delete(`/shipping-lines/${id}`);

// Employees (for agent list)
export const getAgentsList = () => api.get('/employees');

// Ports (Dédouanement / Customs Clearance)
export const getPorts = () => api.get('/ports');
export const createPort = (data) => api.post('/ports', data);
export const updatePort = (id, data) => api.put(`/ports/${id}`, data);
export const deletePort = (id) => api.delete(`/ports/${id}`);
export const getPortContainers = (id) => api.get(`/ports/${id}/containers`);

// Loading Groups (dealer / family bulk loading)
export const getLoadingGroups = () => api.get('/loading-groups');

// Car search (for container assignment)
export const searchCars = (params) => api.get('/cars/search', { params });

// Container management
export const updateContainer = (containerNumber, data) =>
  api.put(`/containers/${encodeURIComponent(containerNumber)}`, data);
export const deleteContainer = (containerNumber) =>
  api.delete(`/containers/${encodeURIComponent(containerNumber)}`);
export const removeCarFromContainer = (containerNumber, carId) =>
  api.delete(`/containers/${encodeURIComponent(containerNumber)}/cars/${carId}`);

// AI Chat Assistant
export const sendChatMessage = (data) => api.post('/chat/message', data);
export const listChatSessions = () => api.get('/chat/sessions');
export const getChatHistory = (sessionId) => api.get(`/chat/sessions/${sessionId}`);
export const deleteChatSession = (sessionId) => api.delete(`/chat/sessions/${sessionId}`);

// Automations
export const listAutomations = () => api.get('/automations');
export const createAutomation = (data) => api.post('/automations', data);
export const updateAutomation = (id, data) => api.put(`/automations/${id}`, data);
export const deleteAutomation = (id) => api.delete(`/automations/${id}`);
export const testAutomation = (id, orderId) => api.post(`/automations/${id}/test/${orderId}`);
export const listAutomationRuns = (ruleId) => api.get('/automations/runs', { params: ruleId ? { rule_id: ruleId } : {} });
export const getAutomationPlaceholders = () => api.get('/automations/placeholders');

// Live chat — staff side
export const listChatThreads = (params) => api.get('/chat-threads', { params });
export const getChatThreadMessages = (threadId) => api.get(`/chat-threads/${threadId}/messages`);
export const postChatThreadMessage = (threadId, body) => api.post(`/chat-threads/${threadId}/messages`, { body });
export const uploadChatThreadAttachment = (threadId, file, caption) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/chat-threads/${threadId}/attachments${caption ? `?caption=${encodeURIComponent(caption)}` : ''}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getChatUnreadCount = () => api.get('/chat-threads/unread-count');

// Live chat — dealer portal side
export const portalGetChat = () => api.get('/portal/chat');
export const portalSendChat = (body) => api.post('/portal/chat', { body });
export const portalUploadChatAttachment = (file, caption) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/portal/chat/attachments${caption ? `?caption=${encodeURIComponent(caption)}` : ''}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Order drafts
export const portalSubmitOrderDraft = (data) => api.post('/portal/order-drafts', data);
export const portalListMyDrafts = () => api.get('/portal/order-drafts');
export const portalCancelMyDraft = (id, reason) => api.post(`/portal/order-drafts/${id}/cancel`, reason ? { reason } : {});
export const portalCancelMyOrder = (id, reason) => api.post(`/portal/orders/${id}/cancel`, reason ? { reason } : {});
export const portalListMyBeneficiaries = () => api.get('/portal/beneficiaries');
export const portalCreateBeneficiary = (data) => api.post('/portal/beneficiaries', data);
export const portalUpdateBeneficiary = (id, data) => api.put(`/portal/beneficiaries/${id}`, data);
export const portalListBeneficiaryDocs = (id) => api.get(`/portal/beneficiaries/${id}/documents`);
export const portalUploadBeneficiaryDoc = (id, formData) => api.post(`/portal/beneficiaries/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const portalDeleteBeneficiaryDoc = (benId, docId) => api.delete(`/portal/beneficiaries/${benId}/documents/${docId}`);
export const portalListCatalogBrands = () => api.get('/portal/catalog/brands');
export const portalListCatalogModels = (brandId) => api.get('/portal/catalog/models', { params: brandId ? { brand_id: brandId } : {} });
export const portalListCatalogColors = () => api.get('/portal/catalog/colors');
export const adminListOrderDrafts = (status) => api.get('/order-drafts', { params: status ? { status } : {} });
export const approveOrderDraft = (id, payload) => api.post(`/order-drafts/${id}/approve`, payload || {});
export const rejectOrderDraft = (id, reason) => api.post(`/order-drafts/${id}/reject`, reason ? { reason } : {});
export const cancelOrderDraft = (id, reason) => api.post(`/order-drafts/${id}/cancel`, reason ? { reason } : {});
export const deleteOrderDraft = (id) => api.delete(`/order-drafts/${id}`);
export const getOrderDraftCount = () => api.get('/order-drafts/count');

// Douane (Customs Clearance) Purchase Orders
export const getDouanePos = () => api.get('/douane-pos');
export const getDouanePo = (id) => api.get(`/douane-pos/${id}`);
export const createDouanePo = (data) => api.post('/douane-pos', data);
export const updateDouanePo = (id, data) => api.put(`/douane-pos/${id}`, data);
export const deleteDouanePo = (id) => api.delete(`/douane-pos/${id}`);
export const getDouanePoPdfUrl = (id) => `${API}/api/douane-pos/${id}/pdf`;

// System info — public, unauthenticated. Used to render the DEMO banner.
export const getSystemInfo = () => api.get('/system/info');
// Active company's brand (authenticated) — drives the sidebar/logo reskin.
export const getActiveBrand = () => api.get('/system/brand');

// ===== Multi-tenancy (master_admin only) =====
export const listCompanies = () => api.get('/companies');
export const createCompany = (data) => api.post('/companies', data);
export const getCompany = (id) => api.get(`/companies/${id}`);
export const updateCompany = (id, data) => api.put(`/companies/${id}`, data);
export const deleteCompany = (id) => api.delete(`/companies/${id}`);
export const switchActiveCompany = (id) => api.post(`/companies/${id}/switch`);

export default api;
